import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EmployeeDemographicInfo } from "../types/employeeTypes";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { useSearchParams } from "react-router-dom";
import { useAddressValidation } from "@/hooks/useAddressValidation";
import { useAuth } from "@/hooks/useAuth";
import { isSuperUser } from "@/types/user";

interface DemographicInformationProps {
  demographicInfo: EmployeeDemographicInfo | null;
  setDemographicInfo: React.Dispatch<
    React.SetStateAction<EmployeeDemographicInfo | null>
  >;
  employeeId: string | undefined;
  toast: (props: {
    title: string;
    description: string;
    duration?: number;
  }) => void;
  readOnly?: boolean;
}

interface GenderSelectProps {
  value: "M" | "F" | "" | null;
  onChange: (value: "M" | "F" | "" | null) => void;
  error?: string;
}

const GenderSelect: React.FC<GenderSelectProps> = ({
  value,
  onChange,
  error,
}) => {
  return (
    <div className="space-y-2">
      <Label htmlFor="gender">
        Gender
        <span className="text-red-500 ml-1">*</span>
      </Label>
      <Select
        name="gender"
        value={value || ""}
        onValueChange={(newValue) =>
          onChange(newValue as "M" | "F" | "" | null)
        }
      >
        <SelectTrigger>
          <SelectValue placeholder="Select gender" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="M">Male</SelectItem>
          <SelectItem value="F">Female</SelectItem>
          <SelectItem value="O">Other</SelectItem>
        </SelectContent>
      </Select>
      {error && <p className="text-red-500 text-sm">{error}</p>}
    </div>
  );
};

const DemographicInformation: React.FC<DemographicInformationProps> = ({
  demographicInfo,
  setDemographicInfo,
  employeeId,
  toast,
  readOnly = false,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { user } = useAuth();
  const isSuperUserAccount = isSuperUser(user);
  const [selectedUserId, setSelectedUserId] = useState<string>("current");
  const [searchParams] = useSearchParams();
  
  // Use address validation hook for country-specific validation
  const addressValidation = useAddressValidation();

  // Check for userId param in URL which would be set when a superuser is accessing the page
  useEffect(() => {
    const userIdParam = searchParams.get("userId");
    if (isSuperUserAccount && userIdParam) {
      setSelectedUserId(userIdParam);
    }
  }, [searchParams, isSuperUserAccount]);

  // Function to convert date from YYYY-MM-DD to DD/MM/YYYY
  const formatDateForDisplay = (dateStr: string): string => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };

  // Function to convert date from DD/MM/YYYY to YYYY-MM-DD
  const formatDateForAPI = (dateStr: string): string => {
    if (!dateStr) return "";
    const [day, month, year] = dateStr.split("/");
    return `${year}-${month}-${day}`;
  };

  const handleDemographicInfoChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setDemographicInfo((prev) => {
      if (!prev) return null;
      let updatedInfo;
      if (name.startsWith("address.")) {
        const addressField = name.split(".")[1];
        updatedInfo = {
          ...prev,
          homeAddress: {
            ...prev.homeAddress,
            [addressField]: value,
          },
        };
      } else if (name === "dob") {
        // Handle date input specially
        const formattedDate = formatDateForAPI(value);
        updatedInfo = { ...prev, [name]: formattedDate };
      } else {
        updatedInfo = { ...prev, [name]: value };
      }
      validateField(name, value);
      return updatedInfo;
    });
  };

  const validateField = (name: string, value: string) => {
    let error = "";
    switch (name) {
      case "firstName":
      case "lastName":
        if (value.trim() === "") {
          error = "This field is required";
        }
        if (!validateName(value)) {
          error = "Invalid name. You can only use A-Z characters.";
        }
        break;
      case "address.street_number":
        const streetResult = addressValidation.validateStreetNumber(value);
        if (!streetResult.isValid) {
          error = streetResult.error;
        }
        break;
      case "address.route":
      case "address.locality":
        if (value.trim() === "") {
          error = "This field is required";
        }
        break;
      case "address.postal_code":
        const postalResult = addressValidation.validatePostalCode(value);
        if (!postalResult.isValid) {
          error = postalResult.error;
        }
        break;
      case "dob":
        if (!validateBritishDate(value)) {
          error = "Invalid date format. Please use DD/MM/YYYY";
        }
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateBritishDate = (dateStr: string): boolean => {
    if (!dateStr) return false;
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    if (!regex.test(dateStr)) return false;

    const [day, month, year] = dateStr.split("/").map(Number);
    const date = new Date(year, month - 1, day);
    return (
      date.getDate() === day &&
      date.getMonth() === month - 1 &&
      date.getFullYear() === year
    );
  };

  const validateName = (name: string) => {
    const nameRegex = /^[A-Za-z]+$/;
    return nameRegex.test(name);
  };

  const validateStreetNumber = (streetNumber: string) => {
    const streetNumberRegex = /^[0-9A-Za-z]+$/;
    return streetNumberRegex.test(streetNumber);
  };

  const validatePostcode = (postcode: string) => {
    const result = addressValidation.validatePostalCode(postcode);
    return result.isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      if (demographicInfo) {
        if (!validatePostcode(demographicInfo.homeAddress.postal_code)) {
          toast({
            title: "Error",
            description: "Invalid postcode format",
            duration: 3000,
          });
          return;
        }

        if (
          demographicInfo.homeAddress.route.length < 1 ||
          demographicInfo.homeAddress.locality.length < 1
        ) {
          toast({
            title: "Error",
            description:
              "Street name, and town/city are required",
            duration: 3000,
          });
          return;
        }

        // Validate date format before submission
        if (
          demographicInfo.dob &&
          !validateBritishDate(formatDateForDisplay(demographicInfo.dob))
        ) {
          toast({
            title: "Error",
            description: "Invalid date format. Please use DD/MM/YYYY",
            duration: 3000,
          });
          return;
        }
      }

      if (employeeId) {
        // Update existing employee
        await ApiService.request(EndpointKeys.UPDATE_DEMOGRAPHIC_INFO, {
          method: "POST",
          body: demographicInfo,
          urlParams: { employeeId },
          // Include userId in queryParams if superuser is accessing another user's employee
          ...(isSuperUserAccount &&
            selectedUserId !== "current" && {
              queryParams: { userId: selectedUserId },
            }),
        });
      } else {
        // Create new employee
        await ApiService.request(EndpointKeys.CREATE_DEMOGRAPHIC_INFO, {
          method: "POST",
          body: demographicInfo,
          // Include userId in queryParams if superuser is accessing another user's employee
          ...(isSuperUserAccount &&
            selectedUserId !== "current" && {
              queryParams: { userId: selectedUserId },
            }),
        });
      }

      console.log("Demographic information updated successfully");
      toast({
        title: "Success",
        description:
          "The demographic information has been updated successfully",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error updating demographic information:", error);
      toast({
        title: "Error",
        description: "Failed to update demographic information",
        duration: 3000,
      });
    }
  };

  const renderInput = (
    name: string,
    label: string,
    value: string,
    required: boolean = false,
    type: string = "text",
  ) => {
    // Special handling for date input
    if (type === "date") {
      return (
        <div>
          <Label htmlFor={name}>
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          <Input
            id={name}
            name={name}
            value={formatDateForDisplay(value)}
            onChange={handleDemographicInfoChange}
            required={required}
            type="text"
            placeholder="DD/MM/YYYY"
          />
          {errors[name] && (
            <p className="text-red-500 text-sm mt-1">{errors[name]}</p>
          )}
        </div>
      );
    }

    return (
      <div>
        <Label htmlFor={name}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
        <Input
          id={name}
          name={name}
          value={value}
          onChange={handleDemographicInfoChange}
          required={required}
          type={type}
        />
        {errors[name] && (
          <p className="text-red-500 text-sm mt-1">{errors[name]}</p>
        )}
      </div>
    );
  };

  if (!demographicInfo) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-2xl font-semibold mb-4">Demographic Information</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderInput(
              "firstName",
              "First Name(s)",
              demographicInfo.firstName,
              true,
            )}
            {renderInput(
              "lastName",
              "Last Name",
              demographicInfo.lastName,
              true,
            )}
            {renderInput(
              "address.street_number",
              "Street Number",
              demographicInfo.homeAddress.street_number,
            )}
            {renderInput(
              "address.route",
              "Street Name",
              demographicInfo.homeAddress.route,
              true,
            )}
            {renderInput(
              "address.locality",
              "Town/City",
              demographicInfo.homeAddress.locality,
              true,
            )}
            {renderInput(
              "address.state",
              "County",
              demographicInfo.homeAddress.state,
            )}
            {renderInput(
              "address.postal_code",
              "Postcode",
              demographicInfo.homeAddress.postal_code,
              true,
            )}
            {renderInput(
              "address.country",
              "Country",
              demographicInfo.homeAddress.country || "United Kingdom",
              true,
            )}
            {renderInput(
              "phoneNumber",
              "Phone Number",
              demographicInfo.phoneNumber || "",
            )}

            <GenderSelect
              value={demographicInfo.gender as "M" | "F" | ""}
              onChange={(value) => {
                handleDemographicInfoChange({
                  target: { name: "gender", value },
                } as React.ChangeEvent<HTMLSelectElement>);
              }}
              error={errors.gender}
            />
            {renderInput(
              "dob",
              "Date of Birth",
              demographicInfo.dob || "",
              true,
              "date",
            )}
          </div>

          <Button type="submit" className="w-full">
            Update Demographic Information
          </Button>
        </form>
      </div>
    </div>
  );
};

export default DemographicInformation;
